import type { UseFormReturnType } from "@mantine/form";
import type { Collaborators } from "@src/components/board/schema";
import { UserActivationStatus } from "@src/gql/graphql";
import { useEffect } from "react";
import type { z } from "zod";
import { getShareWhiteboardUserStateFromPermission } from "../whiteboard-invite-fns";
import type { inviteFormSchema } from "../whiteboard-invite-schema";

/**
 * Hook to sync collaborator data from the whiteboard to the form
 */
export function useSyncCollaboratorsUpdateToForm(
	form: UseFormReturnType<z.infer<typeof inviteFormSchema>>,
	collaborators: Collaborators,
) {
	// biome-ignore lint/correctness/useExhaustiveDependencies(form.setFieldValue): form.setFieldValue will cause infinite loop
	// biome-ignore lint/correctness/useExhaustiveDependencies(form.values.users.findIndex): form.values.users.findIndex will cause infinite loop
	useEffect(() => {
		if (!collaborators) {
			return;
		}

		for (const collaborator of collaborators) {
			const userEmail = collaborator.details?.email;
			if (!userEmail) continue;

			const existingUserIndex = form.values.users.findIndex(
				(user) => user.email === userEmail,
			);

			if (existingUserIndex !== -1) {
				form.setFieldValue(
					`users.${existingUserIndex}.permission`,
					getShareWhiteboardUserStateFromPermission(collaborator.permission),
				);

				form.setFieldValue(
					`users.${existingUserIndex}.activationStatus`,
					collaborator.details?.activationStatus ??
						UserActivationStatus.Unknown,
				);

				form.setFieldValue(
					`users.${existingUserIndex}.avatarUrl`,
					collaborator.details?.avatarUrl ?? "",
				);
			}
		}
	}, [collaborators]);
}
